import 'intersection-observer';
import 'element.scrollintoviewifneeded-polyfill';
import 'pixi.js/unsafe-eval';
import 'pixi.js/advanced-blend-modes';

import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Route } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import polyfillCssAnchoring from '@oddbird/css-anchor-positioning/fn';

import ErrorBoundary from 'js/shared/components/ErrorBoundaryView';
import { getHistory } from 'js/store/history';
import { store, initializeElectronReduxBridge } from 'js/store';
import initPixiExtensions from 'js/playback/pixi-player/initPixiExtensions';

import App from './App.jsx';
import config from './config/config.js';
import initSentry from './logging/index.js';
import theme from './theme.js';
import { RendererContextProvider } from './shared/contexts/RendererContext';
import { GoogleTagManager } from './shared/components/ThirdPartyScripts/GoogleTagManager';
import { Facebook } from './shared/components/ThirdPartyScripts/Facebook';

if (!('anchorName' in document.documentElement.style)) {
  polyfillCssAnchoring();
}

initPixiExtensions();

import '@sparkol/videoscribe-react-ui/dist/style.css';
import './shims/ios.js';
import './shared/helpers/get-user-media.js';

import '../css/main.css';
import 'animate.css';

initSentry({
  dsn: config.SENTRY_DSN,
  release: config.SENTRY_RELEASE,
  environment: config.SENTRY_ENVIRONMENT,
  allowUrls: config.SENTRY_ALLOW_URLS
});

if (import.meta.env.MODE !== 'production') {
  console.info(`Application running in ${import.meta.env.MODE} mode`);
}

function Index() {
  useEffect(() => {
    initializeElectronReduxBridge();
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <ErrorBoundary>
          <Provider store={store}>
            <ConnectedRouter history={getHistory()}>
              <RendererContextProvider>
                <Route component={App} />
                <GoogleTagManager />
                <Facebook />
              </RendererContextProvider>
            </ConnectedRouter>
          </Provider>
        </ErrorBoundary>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}
const root = createRoot(rootElement);

root.render(<Index />);
